import { saveLocal } from '../../../src/libs/browser/storage';
import { ClientPrefix } from 'matrix-js-sdk';
import * as sdk from 'matrix-js-sdk';
import { crossSigningCallbacks } from './security';
import { resetCryptoReady, setCryptoInitPromise } from './cryptoReady';
import store from './store';
import { initKeySharing } from './keySharing';
import { installCryptoReduxBridge } from './cryptoEventBridge';
import { store as reduxStore } from '../../store';
import analyzer from './trafficAnalyzer';
let CUSTOM_HOMESERVER;
/** Base client options shared across all createClient() call sites. */
const baseClientOpts = () => ({
    timelineSupport: true,
    useAuthorizationHeader: true,
    verificationMethods: ['m.sas.v1'],
    cryptoCallbacks: crossSigningCallbacks,
    ...(analyzer ? { fetchFn: analyzer.createFetchInterceptor(globalThis.fetch.bind(globalThis)) } : {}),
});
// @ts-ignore
ClientPrefix.V3 = '/_matrix/client/unstable';
/**
 * Clear all Matrix crypto IndexedDB databases to recover from corruption
 */
const clearCryptoDatabases = async () => {
    try {
        const databases = await indexedDB.databases();
        const cryptoDbNames = databases
            .map((db) => db.name)
            .filter((name) => name != null && (name.includes('matrix-js-sdk') ||
            name.includes('matrix_sdk_crypto') ||
            name.includes('crypto') ||
            name.includes('mapp-chat')));
        await Promise.all(cryptoDbNames.map((name) => new Promise((resolve) => {
            const request = indexedDB.deleteDatabase(name);
            request.onsuccess = () => {
                console.log(`Deleted crypto database: ${name}`);
                resolve();
            };
            request.onerror = () => {
                console.warn(`Failed to delete database: ${name}`);
                resolve();
            };
            request.onblocked = () => {
                console.warn(`Database deletion blocked: ${name}`);
                resolve();
            };
        })));
    }
    catch (e) {
        console.warn('Failed to enumerate IndexedDB databases:', e);
    }
};
const loginWithTokenInner = async (loginResponse, retryCount = 0) => {
    const url = CUSTOM_HOMESERVER || loginResponse.homeServer || store.client.baseUrl;
    // Clear stores if logging in as a different user to prevent account mismatch
    try {
        const currentUserId = store.client.getUserId();
        if (currentUserId && currentUserId !== loginResponse.userId) {
            console.log('Different user detected, clearing stores...');
            await store.client.clearStores();
        }
    }
    catch (e) {
        // Ignore errors if client isn't initialized yet
        console.debug('No previous user session:', e);
    }
    store.client = sdk.createClient({
        ...baseClientOpts(),
        baseUrl: 'https://' + url,
        ...loginResponse,
    });
    try {
        await store.client.initRustCrypto();
    }
    catch (cryptoError) {
        console.error('Failed to initialize crypto store:', cryptoError);
        // If this is the first failure, try clearing corrupted databases and retry
        if (retryCount < 1) {
            console.log('Attempting to recover by clearing crypto databases...');
            await clearCryptoDatabases();
            await store.client.clearStores();
            // Recreate client and retry
            store.client = sdk.createClient({
                ...baseClientOpts(),
                baseUrl: 'https://' + url,
                ...loginResponse,
            });
            return loginWithTokenInner(loginResponse, retryCount + 1);
        }
        throw cryptoError;
    }
    const crypto = store.client.getCrypto();
    await store.client.startClient({ initialSyncLimit: 12 });
    analyzer?.attachEventListeners(store.client);
    const userId = store.client.getUserId();
    if (userId === null)
        throw new Error('UserID is null.');
    if (crypto) {
        // Force a fresh device-key fetch so OTK upload / keys/query complete before the UI sends.
        await crypto.getUserDeviceInfo([userId], true);
    }
    // Bootstrap cross-signing and enable key backup so other devices
    // trust this device and share room keys with it.
    await initKeySharing();
    // Attach crypto→Redux bridge before the chat UI mounts so room keys
    // that arrive during backup checks still update decrypted messages.
    installCryptoReduxBridge(store.client, reduxStore.dispatch);
    return store.client.isLoggedIn();
};
const loginWithToken = (loginResponse, retryCount = 0) => {
    const promise = loginWithTokenInner(loginResponse, retryCount);
    setCryptoInitPromise(promise);
    return promise;
};
const login = async (username, password, url, token) => {
    CUSTOM_HOMESERVER = url;
    if (CUSTOM_HOMESERVER)
        store.client.baseUrl = CUSTOM_HOMESERVER;
    try {
        // Stop the client first to release any open IndexedDB locks,
        // otherwise the Rust crypto database deletion gets blocked.
        store.client.stopClient();
        await store.client.clearStores();
        await clearCryptoDatabases();
    }
    catch (e) {
        console.debug('No stores to clear:', e);
    }
    store.client = sdk.createClient({
        ...baseClientOpts(),
        baseUrl: url || store.client.baseUrl,
    });
    let response;
    if (token) {
        response = await store.client.login('m.login.token', { token });
    }
    else {
        response = await store.client.login('m.login.password', {
            identifier: {
                address: username,
                medium: 'email',
                type: 'm.id.thirdparty',
            },
            password,
        });
    }
    const loginResponse = {
        accessToken: response.access_token,
        userId: response.user_id,
        deviceId: response.device_id,
        homeServer: response.home_server,
    };
    store.client = sdk.createClient({
        ...baseClientOpts(),
        baseUrl: url || store.client.baseUrl,
        ...loginResponse,
    });
    // Initialize Rust crypto. If a stale IndexedDB store is present (e.g.
    // because the blocked-deletion in clearCryptoDatabases() left it behind),
    // clear everything and retry once.
    try {
        await store.client.initRustCrypto();
    }
    catch (cryptoError) {
        console.error('initRustCrypto failed during login, attempting recovery:', cryptoError);
        store.client.stopClient();
        await store.client.clearStores();
        await clearCryptoDatabases();
        store.client = sdk.createClient({
            ...baseClientOpts(),
            baseUrl: url || store.client.baseUrl,
            ...loginResponse,
        });
        await store.client.initRustCrypto();
    }
    saveLocal('chat', loginResponse);
    return loginResponse;
};
export const logout = async () => {
    const chatTheme = localStorage.getItem('chat-theme');
    try {
        store.client.stopClient();
        await store.client.logout();
        await store.client.clearStores();
    }
    catch (e) {
        console.error(e);
    }
    resetCryptoReady();
    // Clear all crypto IndexedDB databases
    await clearCryptoDatabases();
    localStorage.clear();
    if (chatTheme) {
        localStorage.setItem('chat-theme', chatTheme);
    }
};
const isLoggedIn = async () => {
    try {
        const isLoggedInCheck = store.client.isLoggedIn();
        if (!isLoggedInCheck) {
            await logout();
        }
        return isLoggedInCheck;
    }
    catch (e) {
        console.error(e);
        return false;
    }
};
const loginwithsso = async (token) => {
    const res = fetch(`${process.env.MATRIX_URL}/_matrix/client/r0/login`, {
        method: 'POST',
        body: JSON.stringify({
            type: 'm.login.token',
            token,
            initial_device_display_name: 'My device',
        }),
    });
    return res;
};
/**
 * Recover from crypto store corruption by clearing databases and re-initializing
 * Returns true if recovery was successful
 */
export const recoverCryptoStore = async () => {
    try {
        console.log('Attempting crypto store recovery...');
        store.client.stopClient();
        await store.client.clearStores();
        await clearCryptoDatabases();
        // Reload stored auth and re-initialize
        const savedAuth = localStorage.getItem('chat');
        if (savedAuth) {
            const authData = JSON.parse(savedAuth);
            await loginWithToken(authData, 0);
            return true;
        }
        return false;
    }
    catch (e) {
        console.error('Crypto store recovery failed:', e);
        return false;
    }
};
export default {
    login,
    loginWithToken,
    logout,
    isLoggedIn,
    loginwithsso,
    recoverCryptoStore,
};
